import {
    ALL_REVIEW_GATE_OPTIONS,
    REVIEW_GATE_STATUS,
    REVIEW_GATE_STATUS_CLASS
} from "../../recommendations/utils/qaGateValidation";
import { getCombBandChildren, isExecutiveSummaryCombBand } from "./executiveSummaryUtils";

export { REVIEW_GATE_STATUS, REVIEW_GATE_STATUS_CLASS, ALL_REVIEW_GATE_OPTIONS };

const VALID_QA_STATUSES = new Set(Object.values(REVIEW_GATE_STATUS));

export function isExecutiveSummaryLeafBand(band) {
    return band != null && typeof band === "object" && !isExecutiveSummaryCombBand(band);
}

export function normalizeExecutiveSummaryBandQaStatus(status) {
    const value = String(status ?? "").trim();
    return VALID_QA_STATUSES.has(value) ? value : REVIEW_GATE_STATUS.IN_PROGRESS;
}

export function getExecutiveSummaryBandQaStatus(band) {
    if (!isExecutiveSummaryLeafBand(band)) return null;
    return normalizeExecutiveSummaryBandQaStatus(band.qa_status);
}

export function isExecutiveSummaryBandContentLocked(band) {
    const status = getExecutiveSummaryBandQaStatus(band);
    return status === REVIEW_GATE_STATUS.READY_FOR_QA || status === REVIEW_GATE_STATUS.QA_PASSED;
}

const EXEC_SUM_BAND_CONTENT_PATCH_KEYS = new Set(["value", "data", "images", "image_layout", "config"]);

export function isExecutiveSummaryBandContentPatch(partial) {
    if (!partial || typeof partial !== "object") return false;
    return Object.keys(partial).some(key => EXEC_SUM_BAND_CONTENT_PATCH_KEYS.has(key));
}

export function flattenExecutiveSummaryLeafBands(bands) {
    const result = [];
    const walk = list => {
        if (!Array.isArray(list)) return;
        list.forEach(band => {
            if (!band || typeof band !== "object") return;
            if (isExecutiveSummaryCombBand(band)) {
                walk(getCombBandChildren(band));
            } else {
                result.push(band);
            }
        });
    };
    walk(bands);
    return result;
}

export function getExecutiveSummaryQaStatusBreakdown(bands) {
    const leafBands = flattenExecutiveSummaryLeafBands(bands);
    const totalCount = leafBands.length;
    const counts = {
        [REVIEW_GATE_STATUS.IN_PROGRESS]: 0,
        [REVIEW_GATE_STATUS.READY_FOR_QA]: 0,
        [REVIEW_GATE_STATUS.QA_PASSED]: 0
    };

    leafBands.forEach(band => {
        const status = getExecutiveSummaryBandQaStatus(band);
        if (status && counts[status] != null) {
            counts[status] += 1;
        }
    });

    const readyCount = counts[REVIEW_GATE_STATUS.READY_FOR_QA] + counts[REVIEW_GATE_STATUS.QA_PASSED];
    const percent = totalCount ? Math.round((readyCount / totalCount) * 100) : 0;

    return {
        totalCount,
        readyCount,
        percent,
        counts: {
            in_progress: counts[REVIEW_GATE_STATUS.IN_PROGRESS],
            ready_for_qa: counts[REVIEW_GATE_STATUS.READY_FOR_QA],
            qa_passed: counts[REVIEW_GATE_STATUS.QA_PASSED]
        }
    };
}

export function getExecutiveSummaryQaCompletion(bands) {
    const { readyCount, totalCount, percent } = getExecutiveSummaryQaStatusBreakdown(bands);
    return { readyCount, totalCount, percent };
}

export function getAllowedExecutiveSummaryQaOptions({ canEdit = false, canMarkQaPassed = false } = {}) {
    if (!canEdit) return [];
    if (canMarkQaPassed) return ALL_REVIEW_GATE_OPTIONS;
    return ALL_REVIEW_GATE_OPTIONS.filter(opt => opt.value !== REVIEW_GATE_STATUS.QA_PASSED);
}

export const EXEC_SUM_QA_OPTION_COLORS = {
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "#6b778c",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "#2684ff",
    [REVIEW_GATE_STATUS.QA_PASSED]: "#36b37e"
};

export const EXECUTIVE_SUMMARY_BUILDING_QA_STATUS = {
    IN_PROGRESS: REVIEW_GATE_STATUS.IN_PROGRESS,
    READY_FOR_QA: REVIEW_GATE_STATUS.READY_FOR_QA,
    QA_PASSED: REVIEW_GATE_STATUS.QA_PASSED
};

export function getExecutiveSummaryBuildingQaStatus(bands) {
    const leafBands = flattenExecutiveSummaryLeafBands(bands);
    if (!leafBands.length) {
        return EXECUTIVE_SUMMARY_BUILDING_QA_STATUS.IN_PROGRESS;
    }

    const statuses = leafBands.map(band => getExecutiveSummaryBandQaStatus(band));
    const allPassed = statuses.every(status => status === REVIEW_GATE_STATUS.QA_PASSED);
    if (allPassed) {
        return EXECUTIVE_SUMMARY_BUILDING_QA_STATUS.QA_PASSED;
    }

    const allReadyForQa = statuses.every(status => status === REVIEW_GATE_STATUS.READY_FOR_QA);
    if (allReadyForQa) {
        return EXECUTIVE_SUMMARY_BUILDING_QA_STATUS.READY_FOR_QA;
    }

    const hasInProgress = statuses.some(status => status === REVIEW_GATE_STATUS.IN_PROGRESS);
    if (!hasInProgress) {
        return EXECUTIVE_SUMMARY_BUILDING_QA_STATUS.READY_FOR_QA;
    }

    return EXECUTIVE_SUMMARY_BUILDING_QA_STATUS.IN_PROGRESS;
}

export function getExecutiveSummaryQaStatusLabel(status) {
    const normalized = normalizeExecutiveSummaryBandQaStatus(status);
    const option = ALL_REVIEW_GATE_OPTIONS.find(opt => opt.value === normalized);
    return option?.label ?? "In Progress";
}

export function mapExecutiveSummaryQaApiToFilter(value) {
    if (value === "passed") {
        return REVIEW_GATE_STATUS.QA_PASSED;
    }
    return value;
}

export function appendQaStatusToLeafBandPayload(payload, band) {
    if (!payload || !isExecutiveSummaryLeafBand(band)) return payload;
    return { ...payload, qa_status: getExecutiveSummaryBandQaStatus(band) };
}
